import { isNil } from 'ramda'

import type {
  ArticleListResponse,
  ArticleResponse,
  CategoryListResponse,
  CategoryListResponseDataItemCustom,
  CategoryResponse,
  GlobalResponse,
  NavigationResponse,
  PageListResponse,
  PageResponse,
  PageResponseDataObject,
  SearchArticlesResponse,
} from '@twisto/api-cms'
import type { AdditionalDataAPI } from '@twisto/api-cms/custom-types'

import type { SectionType } from '../components'

export const PAGES_WITH_ADDITIONAL_DATA = {
  '/pages': 'eshops/popular/',
}

export const getStrapiURL = (path: string) =>
  `${process.env.STRAPI_API_URL ?? 'http://localhost:1337/api'}${path}`

export const getQueryString = (
  params: Record<string, string | string[] | undefined>
) => {
  const query = Object.entries(params)
    .map(([key, value]) => `${key}=${value}`)
    .join('&')

  return query.length > 0 ? `?${query}` : ''
}

const fetchAPI = async (
  requestUrl: string,
  urlParamsObject = {},
  options = {}
) => {
  const mergedOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
    ...options,
  }

  const queryString = getQueryString(urlParamsObject)
  try {
    const response = await fetch(`${requestUrl}${queryString}`, mergedOptions)
    const data = await response.json()

    return data
  } catch (error) {
    return {}
  }
}

const fetchStrapiAPI = async (
  path: string,
  urlParamsObject = {},
  options = {}
) => {
  const requestUrl = getStrapiURL(path)
  // eslint-disable-next-line no-console
  console.log(requestUrl)

  return await fetchAPI(requestUrl, urlParamsObject, options)
}

export const getPages = async (): Promise<PageListResponse> =>
  await fetchStrapiAPI('/pages', { fields: 'path' })

export const getPage = async (
  url: string
): Promise<
  PageResponse & {
    data: PageResponseDataObject & {
      attributes: { contentSections?: SectionType[] }
    }
  }
> => await fetchStrapiAPI(`/page/${encodeURIComponent(url)}`)

export const getNavigation = async (id: number): Promise<NavigationResponse> =>
  await fetchStrapiAPI(`/navigations/${id}`)

export const getGlobalMetadata = async (): Promise<GlobalResponse> =>
  await fetchStrapiAPI('/global')

export const getCategories = async (): Promise<CategoryListResponse> =>
  await fetchStrapiAPI('/categories', { fields: 'path' })

export const getCategory = async (
  url: string
): Promise<
  CategoryResponse & {
    data: CategoryListResponseDataItemCustom
  }
> => await fetchStrapiAPI(`/categories/${encodeURIComponent(url)}`)

export const getArticle = async (url: string): Promise<ArticleResponse> =>
  await fetchStrapiAPI(`/articles/${encodeURIComponent(url)}`)

export const getArticles = async (): Promise<ArticleListResponse> =>
  await fetchStrapiAPI('/articles', { fields: 'path' })

export const getPopularArticles = async (
  rootCategoryId?: number
): Promise<ArticleListResponse> =>
  await fetchStrapiAPI('/articles', {
    'filters[isPopular]': true,
    'pagination[pageSize]': 15,
    ...(!isNil(rootCategoryId) && {
      'filters[rootCategoryId]': rootCategoryId,
    }),
  })

export const searchArticles = async (
  searchTerm: string,
  pageSize: number,
  rootCategoryId?: number
): Promise<SearchArticlesResponse> =>
  await fetchAPI('/api/article-search', {
    query: encodeURIComponent(searchTerm),
    'pagination[articles][pageSize]': pageSize,
    ...(!isNil(rootCategoryId) && {
      'filters[articles][rootCategoryId]': rootCategoryId,
    }),
  })

export const getAdditionalData = async (
  pageUrl: keyof typeof PAGES_WITH_ADDITIONAL_DATA
): Promise<AdditionalDataAPI | null> => {
  const rawStrapiUrl = process.env.STRAPI_API_URL ?? ''
  const apiUrl = rawStrapiUrl
    .replace(/https?:\/\/cms\./, 'https://api.')
    .replace(/\/api$/, '')

  const requestUrl = PAGES_WITH_ADDITIONAL_DATA[pageUrl]
  const url = `${apiUrl}/${requestUrl}`

  if (!requestUrl) {
    return null
  }

  return await fetchAPI(url)
}
